import React, { Component } from "react";
import { GET, POST, PUT, DELETE, Modal, DatePicker, alert, loading, t } from "./CustomComponent.js";
import { can } from "../utils/permission.js";
import { getCurrentCompany, getProfile } from "../utils/localStorage";
import { formatNumber } from "../utils/formatting";

/**
 * ใบขอเบิกอะไหล่เพิ่มเติม (Picking List Request) ของ WO Normal Sales — การ์ด 6ae4b231
 * แสดงการ์ด + modal สร้าง/แก้ไขร่าง + modal ดูใบที่บันทึกแล้ว + พิมพ์ A4
 * ปุ่มในการ์ดเป็น span role="button" เพราะ panel ถูกครอบด้วย <fieldset disabled> ของ modal WO (โหมดดูอย่างเดียว)
 * ซึ่งปิด <button> ทุกตัว แต่ผู้ใช้ยังต้องดู/พิมพ์ใบเดิมได้
 */

const BASE = "admin/wo-picking-lists";
const REASON_OTHER = "other";

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${("0" + (d.getMonth() + 1)).slice(-2)}-${("0" + d.getDate()).slice(-2)}`;
};
const fmtD = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "-");
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);

const Act = ({ onClick, disabled, className = "", title, style, children }) => (
    <span
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled ? "true" : undefined}
        title={title}
        className={`${className} ${disabled ? "opacity-50" : "pointer"}`}
        style={{ cursor: disabled ? "not-allowed" : "pointer", userSelect: "none", ...style }}
        onClick={disabled ? undefined : onClick}
        onKeyDown={(e) => {
            if (!disabled && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onClick && onClick(e);
            }
        }}
    >
        {children}
    </span>
);

const emptyEditor = (ctx, pl) => ({
    uid: pl?.uid || null,
    warehouse_uid: pl?.warehouse?.uid || ctx?.default_warehouse?.uid || ctx?.warehouses?.[0]?.uid || "",
    need_date: pl?.need_date ? String(pl.need_date).slice(0, 10) : "",
    reason: pl?.reason || "",
    cost_center: pl?.cost_center || ctx?.default_cost_center || "",
    remark: pl?.remark || "",
    requester_name: pl?.requester?.name || getProfile()?.full_name || "",
    items: (pl?.items || []).map((i) => ({
        part_uid: i.part_uid,
        part_number: i.part_number,
        part_name: i.part_name,
        uom_name: i.uom_name,
        quantity: i.quantity,
        available_qty: null,
        in_so_qty: 0,
        ack: false,
    })),
    q: "",
    results: null,
    searching: false,
    banner: null,
    errors: {},
    tried: false,
    dirty: false,
    saving: false,
});

/**
 * ตาราง "อะไหล่ที่เบิกเพิ่ม" ในแท็บอะไหล่ของ WO Normal Sales (wireframe ข้อ 7)
 * รวมจำนวนต่อรหัสอะไหล่จากใบ status saved เท่านั้น (ไม่รวมร่าง) พร้อมเลขใบขอเบิกที่อ้างอิง
 */
export const WoPickingExtraPartsTable = ({ lists }) => {
    const agg = new Map();
    (lists || [])
        .filter((pl) => pl.status === "saved")
        .forEach((pl) =>
            (pl.items || []).forEach((it) => {
                const key = it.part_uid || it.part_number;
                const row = agg.get(key) || { part_number: it.part_number, part_name: it.part_name, uom_name: it.uom_name, qty: 0, refs: [] };
                row.qty += Number(it.quantity || 0);
                if (!row.refs.includes(pl.pl_number)) row.refs.push(pl.pl_number);
                agg.set(key, row);
            }),
        );
    const rows = Array.from(agg.values());
    return (
        <div className="mt-4">
            <div className="fw-bold text-14 mb-2">{t("pl_extra_parts_title")}</div>
            {rows.length ? (
                <div className="border rounded-3 bg-white overflow-hidden">
                    <table className="table table-sm mb-0 text-13">
                        <thead>
                            <tr>
                                <th className="fw-bold">{t("part_code")}</th>
                                <th className="fw-bold">{t("pl_extra_col_name")}</th>
                                <th className="fw-bold text-end">{t("pl_extra_col_total")}</th>
                                <th className="fw-bold">{t("unit")}</th>
                                <th className="fw-bold">{t("pl_extra_col_ref")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.part_number}>
                                    <td>{r.part_number}</td>
                                    <td>{r.part_name}</td>
                                    <td className="text-end">{formatNumber(r.qty)}</td>
                                    <td>{r.uom_name || "-"}</td>
                                    <td>{r.refs.join(", ")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="border rounded-3 p-3 bg-white text-secondary text-12 text-center">{t("pl_extra_parts_empty")}</div>
            )}
        </div>
    );
};

export default class WoPickingListPanel extends Component {
    state = { ctx: null, lists: [], ready: false, editor: null, viewing: null, confirm: null };
    searchTimer = null;
    searchSeq = 0;
    mounted = false;

    componentDidMount() {
        this.mounted = true;
        this.load();
    }

    componentDidUpdate(prev) {
        if (prev.woUid !== this.props.woUid) this.load();
    }

    componentWillUnmount() {
        this.mounted = false;
        clearTimeout(this.searchTimer);
    }

    setSafe = (s) => this.mounted && this.setState(s);

    load = async () => {
        const { woUid } = this.props;
        if (!woUid || !can("wo_picking_list.read")) return;
        const [ctx, lists] = await Promise.all([GET(`${BASE}/context/${woUid}`, null, true, { skipAccessDeniedAlert: true }), GET(BASE, { wo_uid: woUid }, true, { skipAccessDeniedAlert: true })]);
        const nextLists = lists?.status && Array.isArray(lists.data) ? lists.data : [];
        this.setSafe({ ctx: ctx?.status ? ctx.data : null, lists: nextLists, ready: true });
        // แจ้ง parent ทุกครั้งที่โหลด/บันทึก/ลบ เพื่อให้ตาราง "อะไหล่ที่เบิกเพิ่ม" ในแท็บอะไหล่อัปเดตตาม
        if (this.mounted && this.props.onListsChange) this.props.onListsChange(nextLists);
    };

    // ───────── helpers ─────────
    reasonLabel = (value) => (this.state.ctx?.reasons || []).find((r) => r.value === value)?.label || value || "-";
    ccLabel = (code, sep = " - ") => {
        const c = (this.state.ctx?.cost_centers || []).find((x) => x.code === code);
        return c ? `${c.code}${sep}${c.name}` : code || "-";
    };

    // ───────── editor ─────────
    openEditor = async (pl) => {
        const editor = emptyEditor(this.state.ctx, pl);
        this.setState({ editor });
        if (editor.items.length) await this.refreshItemStock(editor.warehouse_uid, editor.items);
    };

    patchEditor = (patch, markDirty = true) => this.setState((s) => (s.editor ? { editor: { ...s.editor, ...patch, ...(markDirty ? { dirty: true } : {}) } } : null));

    fetchParts = (search, warehouse_uid) => GET(`${BASE}/parts`, { wo_uid: this.props.woUid, search: search || "", ...(warehouse_uid ? { warehouse_uid } : {}) });

    // ตรวจจำนวนคงเหลือของรายการที่อยู่ในใบใหม่ (เปิดร่างเดิม / เปลี่ยนคลัง) — ค้นด้วยรหัสอะไหล่แล้วจับคู่ part_uid
    refreshItemStock = async (warehouse_uid, itemsArg) => {
        const items = itemsArg || this.state.editor?.items || [];
        const found = await Promise.all(
            items.map(async (it) => {
                const r = await this.fetchParts(it.part_number, warehouse_uid);
                return (r?.status && Array.isArray(r.data) ? r.data : []).find((p) => p.part_uid === it.part_uid) || null;
            }),
        );
        this.setState((s) =>
            s.editor
                ? {
                      editor: {
                          ...s.editor,
                          items: s.editor.items.map((it, ix) => ({
                              ...it,
                              available_qty: found[ix] ? found[ix].available_qty : null,
                              in_so_qty: found[ix] ? found[ix].in_so_qty : it.in_so_qty,
                          })),
                      },
                  }
                : null,
        );
    };

    onChangeWarehouse = async (warehouse_uid) => {
        this.patchEditor({ warehouse_uid, results: null, q: "", banner: null });
        await this.refreshItemStock(warehouse_uid, this.state.editor?.items);
    };

    onSearch = (q) => {
        this.patchEditor({ q, searching: true }, false);
        clearTimeout(this.searchTimer);
        const seq = ++this.searchSeq;
        this.searchTimer = setTimeout(async () => {
            const ed = this.state.editor;
            if (!ed) return;
            const r = await this.fetchParts(q.trim(), ed.warehouse_uid);
            if (seq !== this.searchSeq || !this.mounted) return;
            this.patchEditor({ results: r?.status && Array.isArray(r.data) ? r.data : [], searching: false }, false);
        }, 350);
    };

    addPart = (p) => {
        const ed = this.state.editor;
        if (ed.items.some((i) => i.part_uid === p.part_uid)) return;
        this.patchEditor({
            items: [...ed.items, { part_uid: p.part_uid, part_number: p.part_number, part_name: p.part_name, uom_name: p.uom_name, quantity: 1, available_qty: p.available_qty, in_so_qty: p.in_so_qty || 0, ack: false }],
            q: "",
            results: null,
            banner: null,
        });
    };

    setItem = (ix, patch) => this.patchEditor({ items: this.state.editor.items.map((it, i) => (i === ix ? { ...it, ...patch } : it)) });
    removeItem = (ix) => this.patchEditor({ items: this.state.editor.items.filter((_, i) => i !== ix) });

    validate = (final) => {
        const ed = this.state.editor;
        const e = {};
        if (final) {
            if (!ed.need_date) e.need_date = t("pl_err_need_date_required");
            else if (ed.need_date < todayStr()) e.need_date = t("pl_err_need_date_past");
            if (!ed.reason) e.reason = t("pl_err_reason_required");
            if (ed.reason === REASON_OTHER && !ed.remark.trim()) e.remark = t("pl_err_remark_required");
            if (!ed.cost_center) e.cost_center = t("pl_err_cost_center_required");
            if (!ed.items.length) e.items = t("pl_err_items_required");
        }
        ed.items.forEach((it, ix) => {
            const q = Number(it.quantity);
            if (it.quantity === "" || !Number.isInteger(q) || q <= 0) e["q" + ix] = t("pl_err_qty_invalid");
            else if (it.available_qty != null && q > it.available_qty) e["q" + ix] = t("pl_err_qty_exceeds").replace("{max}", formatNumber(it.available_qty));
            if (final && it.in_so_qty > 0 && !it.ack) e["a" + ix] = t("pl_err_ack_so");
        });
        return e;
    };

    buildPayload = (isNew) => {
        const ed = this.state.editor;
        return {
            ...(isNew ? { wo_uid: this.props.woUid } : {}),
            warehouse_uid: ed.warehouse_uid,
            need_date: ed.need_date,
            reason: ed.reason,
            remark: ed.remark.trim(),
            cost_center: ed.cost_center,
            items: ed.items.map((i) => ({ part_uid: i.part_uid, quantity: Number(i.quantity) })),
        };
    };

    closeEditor = () => {
        this.searchSeq++;
        clearTimeout(this.searchTimer);
        this.setState({ editor: null });
    };

    tryCloseEditor = () => {
        if (this.state.editor?.dirty) {
            this.setState({ confirm: { title: t("pl_confirm_discard_title"), body: t("pl_confirm_discard_body"), ok: t("pl_discard"), cancel: t("pl_back_to_edit"), danger: true, onOk: this.closeEditor } });
        } else this.closeEditor();
    };

    saveDraft = async () => {
        const ed = this.state.editor;
        const e = this.validate(false);
        if (!ed.items.length && !ed.need_date && !ed.reason && !ed.remark.trim()) {
            this.patchEditor({ banner: { kind: "warn", text: t("pl_err_draft_empty") } }, false);
            return;
        }
        if (Object.keys(e).some((k) => k.startsWith("q"))) {
            this.patchEditor({ errors: e, tried: false, banner: { kind: "warn", text: t("pl_err_fix_qty") } }, false);
            return;
        }
        this.patchEditor({ saving: true }, false);
        loading(true);
        const result = ed.uid ? await PUT(`${BASE}/${ed.uid}`, this.buildPayload(false)) : await POST(BASE, this.buildPayload(true));
        loading(false);
        this.patchEditor({ saving: false }, false);
        if (result?.status) {
            this.closeEditor();
            await this.load();
            alert("", "success", t("success"), t("pl_draft_saved"));
        }
    };

    askSaveFinal = (andPrint) => {
        const ed = this.state.editor;
        const e = this.validate(true);
        this.patchEditor({ tried: true, banner: null }, false);
        if (Object.keys(e).length) {
            alert("", "warning", t("warning"), t("pl_err_fix_red"));
            return;
        }
        const total = ed.items.reduce((a, i) => a + Number(i.quantity || 0), 0);
        this.setState({
            confirm: {
                title: t("pl_confirm_save_title"),
                body: `${formatNumber(ed.items.length)} ${t("pl_items_unit")} · ${formatNumber(total)} ${t("pl_qty_unit")} · CC ${this.ccLabel(ed.cost_center, " – ")}`,
                warn: t("pl_confirm_save_warn"),
                ok: andPrint ? t("pl_save_and_print_short") : t("save"),
                onOk: () => this.saveFinal(andPrint),
            },
        });
    };

    saveFinal = async (andPrint) => {
        const ed = this.state.editor;
        if (!ed) return;
        loading(true);
        let result;
        if (ed.uid) {
            result = await PUT(`${BASE}/${ed.uid}`, this.buildPayload(false));
            if (result?.status) result = await POST(`${BASE}/${ed.uid}/save`, {});
        } else {
            result = await POST(BASE, { ...this.buildPayload(true), submit: true });
        }
        loading(false);
        if (result?.status) {
            this.closeEditor();
            await this.load();
            alert("", "success", t("success"), `${t("pl_saved_with_no")} ${result.data?.pl_number || ""}`);
            if (andPrint && result.data) this.printPl(result.data);
            return;
        }
        // E4: สต็อกถูกเบิกตัดหน้า — api.js ไม่เด้ง alert ให้ แสดงเป็นแบนเนอร์พร้อมอัปเดตคงเหลือล่าสุด
        if (result?.errors?.code === "PL_STOCK_CHANGED") {
            const changed = Array.isArray(result.errors.items) ? result.errors.items : [];
            this.setState((s) =>
                s.editor
                    ? {
                          editor: {
                              ...s.editor,
                              items: s.editor.items.map((it) => {
                                  const c = changed.find((x) => x.part_uid === it.part_uid);
                                  return c ? { ...it, available_qty: Number(c.available) } : it;
                              }),
                              banner: { kind: "error", text: result.message || t("pl_err_stock_changed"), items: changed },
                              tried: true,
                          },
                      }
                    : null,
            );
        }
    };

    deleteDraft = (pl) =>
        this.setState({
            confirm: {
                title: t("pl_confirm_delete_title"),
                body: t("pl_confirm_delete_body"),
                ok: t("pl_delete_draft"),
                danger: true,
                onOk: async () => {
                    loading(true);
                    const r = await DELETE(`${BASE}/${pl.uid}`);
                    loading(false);
                    if (r?.status) {
                        await this.load();
                        alert("", "success", t("success"), t("pl_draft_deleted"));
                    }
                },
            },
        });

    openView = async (pl) => {
        const r = await GET(`${BASE}/${pl.uid}`);
        if (r?.status) this.setState({ viewing: r.data });
    };

    // ───────── print A4 ─────────
    printPl = async (pl) => {
        const ctx = this.state.ctx;
        const co = getCurrentCompany();
        const total = (pl.items || []).reduce((a, i) => a + Number(i.quantity || 0), 0);
        const rows = (pl.items || [])
            .map((i, ix) => `<tr><td>${ix + 1}</td><td>${esc(i.part_number)}</td><td>${esc(i.part_name)}</td><td>${esc(i.bin_location || "")}</td><td class="r">${formatNumber(i.quantity)}</td><td>${esc(i.uom_name)}</td><td></td></tr>`)
            .join("");
        const pad = Array.from({ length: Math.max(0, 5 - (pl.items || []).length) }, () => `<tr><td colspan="7"></td></tr>`).join("");
        const meta = (k, v, span = 1) => `<div style="grid-column:span ${span}"><div class="k">${esc(k)}</div><div class="v">${esc(v)}</div></div>`;
        const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(pl.pl_number)}</title><style>
@page{size:A4 portrait;margin:0}*{box-sizing:border-box}body{margin:0;font-family:Tahoma,Sarabun,sans-serif;font-size:13px;color:#000}
.a4{width:210mm;min-height:296mm;padding:14mm 14mm 10mm;display:flex;flex-direction:column}
.top{display:flex;align-items:center;border-bottom:2px solid #000;padding-bottom:10px}.top .co{font-size:16px;font-weight:700}.top .no{margin-left:auto;text-align:right}.top .no b{font-size:18px}
.ttl{text-align:center;border-bottom:2px solid #000;padding:8px 0}.ttl b{font-size:20px;display:block}.ttl span{font-size:12px;letter-spacing:.5px}
.meta{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px 18px;margin:14px 0}.k{font-size:11.5px;color:#333}.v{font-weight:700}
table{width:100%;border-collapse:collapse;border:1px solid #000}th{background:#eee;border-bottom:1px solid #000;padding:6px;text-align:left;font-size:12px}td{border-bottom:1px solid #999;padding:7px 6px;font-size:12.5px;height:34px}.r{text-align:right}
.signs{margin-top:auto;display:grid;grid-template-columns:repeat(4,1fr);gap:18px;text-align:center;padding-top:60px}.signs div{border-top:1px dotted #000;padding-top:6px;font-size:12px}
.foot{display:flex;justify-content:space-between;border-top:1px solid #999;margin-top:18px;padding-top:6px;font-size:11px;color:#333}
*{-webkit-print-color-adjust:exact;print-color-adjust:exact}</style></head><body><div class="a4">
<div class="top"><div class="co">${esc(co?.name || "")}</div><div class="no">${esc(t("pl_doc_no"))}<br><b>${esc(pl.pl_number)}</b></div></div>
<div class="ttl"><b>${esc(t("pl_print_title"))}</b><span>PICKING LIST REQUEST</span></div>
<div class="meta">
${meta(t("pl_doc_no"), pl.pl_number)}${meta(t("pl_created_at"), pl.saved_at || pl.created_at)}${meta(t("pl_need_date"), fmtD(pl.need_date))}
${meta(t("pl_ref_wo"), pl.wo_number || ctx?.wo?.wo_number)}${meta(t("pl_ref_so"), pl.so_no || ctx?.wo?.so_no)}${meta(t("pl_warehouse"), `${pl.warehouse?.code || ""} ${pl.warehouse?.name || ""}`)}
${meta(t("pl_customer_site"), [ctx?.wo?.customer_name, ctx?.wo?.site].filter(Boolean).join(" · "), 2)}${meta(t("pl_requester"), pl.requester?.name || "-")}
${meta(t("pl_cost_center"), this.ccLabel(pl.cost_center))}${meta(t("pl_reason"), `${this.reasonLabel(pl.reason)}${pl.remark ? " — " + pl.remark : ""}`, 2)}
</div>
<table><thead><tr><th style="width:30px">#</th><th style="width:110px">${esc(t("pl_part_number"))}</th><th>${esc(t("pl_part_name"))}</th><th style="width:90px">${esc(t("pl_bin"))}</th><th class="r" style="width:60px">${esc(t("pl_qty"))}</th><th style="width:50px">${esc(t("pl_uom"))}</th><th class="r" style="width:90px">${esc(t("pl_issued_qty"))}</th></tr></thead>
<tbody>${rows}${pad}<tr><td></td><td></td><td><b>${esc(t("pl_total"))} ${formatNumber((pl.items || []).length)} ${esc(t("pl_items_unit"))}</b></td><td></td><td class="r"><b>${formatNumber(total)}</b></td><td></td><td></td></tr></tbody></table>
<div class="signs">${[t("pl_sign_requester"), t("pl_sign_approver"), t("pl_sign_warehouse"), t("pl_sign_receiver")].map((s) => `<div><b>${esc(s)}</b><br>${esc(t("pl_date"))} ____/____/______</div>`).join("")}</div>
<div class="foot"><span>${esc(t("pl_printed_at"))} ${esc(new Date().toLocaleString("en-GB"))} · ${esc(t("pl_printed_by"))} ${esc(getProfile()?.full_name || "-")}</span><span>${esc(t("pl_page"))} 1/1</span></div>
</div><script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`;
        const w = window.open("", "_blank");
        if (!w) {
            alert("", "warning", t("warning"), t("pl_popup_blocked"));
            return;
        }
        w.document.open();
        w.document.write(html);
        w.document.close();
    };

    // ───────── render ─────────
    renderCard() {
        const { ctx, lists } = this.state;
        const drafts = lists.filter((p) => p.status === "draft");
        const saved = lists.filter((p) => p.status !== "draft");
        const canCreatePerm = can("wo_picking_list.create");
        const canUpdatePerm = can("wo_picking_list.update");
        const canDeletePerm = can("wo_picking_list.delete");
        const createDisabled = !ctx.can_create || !canCreatePerm;
        const why = !ctx.can_create ? ctx.block_reason : !canCreatePerm ? t("access_denied_msg") : "";
        const total = lists.length;
        const draftCount = drafts.length;

        const meta = (p) => (
            <div className="text-12 text-secondary mt-1" style={{ lineHeight: 1.45 }}>
                {formatNumber(p.item_count)} {t("pl_items_unit")} · {p.status === "draft" ? `${t("pl_last_edited")} ${p.updated_at || "-"}` : p.saved_at || p.created_at} · {p.requester?.name || "-"}
                <br />
                CC {this.ccLabel(p.cost_center, " ")}
            </div>
        );

        return (
            <div className="rounded-3 p-3 mb-3 bg-white" style={{ border: "2px solid #2563EB" }}>
                <div className="d-flex align-items-start gap-2">
                    <div>
                        <div className="fw-bold text-16">{t("pl_title")}</div>
                        <div className="text-12 text-secondary">
                            Picking List Request · {formatNumber(total)} {t("pl_doc_unit")}
                            {draftCount ? ` (${t("pl_draft")} ${formatNumber(draftCount)})` : ""}
                        </div>
                    </div>
                    {canCreatePerm && (
                        <Act className="btn btn-primary btn-sm ms-auto text-nowrap" disabled={createDisabled} title={why} onClick={() => this.openEditor(null)}>
                            + {t("pl_create")}
                        </Act>
                    )}
                </div>
                {why && canCreatePerm && <div className="alert alert-warning py-1 px-2 mt-2 mb-0 text-12">{why}</div>}
                {ctx.default_warehouse ? (
                    <div className="bg-light rounded-2 px-2 py-1 mt-2 text-12">
                        <span className="icon me-1">{"\uf023"}</span> {t("pl_wh_allowed")} <b>{ctx.default_warehouse.code} {ctx.default_warehouse.name}</b>{" "}
                        <span className="text-secondary">{(ctx.warehouses || []).length > 1 ? t("pl_wh_yours_changeable") : t("pl_wh_yours")}</span>
                    </div>
                ) : (ctx.warehouses || []).length ? (
                    // ผู้ใช้ที่ไม่ใช่ช่าง (ไม่มีคลังรถของตัวเอง) เลือกคลังในแผนกตอนสร้างใบ
                    <div className="bg-light rounded-2 px-2 py-1 mt-2 text-12">
                        <span className="icon me-1">{"\uf023"}</span> {t("pl_wh_allowed")} <b>{t("pl_wh_dept_count").replace("{n}", ctx.warehouses.length)}</b>{" "}
                        <span className="text-secondary">{t("pl_wh_choose_on_create")}</span>
                    </div>
                ) : null}
                <div className="text-12 text-secondary mt-2">{t("pl_list_caption")}</div>

                {!lists.length ? (
                    <div className="border rounded-3 p-3 text-center text-secondary text-13 mt-3" style={{ borderStyle: "dashed" }}>
                        <div style={{ fontSize: 26 }}>📦</div>
                        <b>{t("pl_empty_title")}</b>
                        <br />
                        {t("pl_empty_desc")}
                    </div>
                ) : (
                    <div className="mt-3">
                        {drafts.map((p) => (
                            <div key={p.uid} className="d-flex align-items-center gap-2 rounded-3 p-2 mb-2 bg-light" style={{ border: "1px dashed #9ca3af" }}>
                                <div className="flex-grow-1 min-w-0">
                                    <Act className="fw-bold text-primary text-decoration-underline" disabled={!canUpdatePerm || !p.permissions?.can_edit || !ctx.can_create} onClick={() => this.openEditor(p)}>
                                        {t("pl_no_number_yet")}
                                    </Act>
                                    <span className="badge bg-secondary ms-2">{t("pl_draft")}</span>
                                    {meta(p)}
                                </div>
                                {canUpdatePerm && (
                                    <Act className="btn btn-outline-secondary btn-sm" title={t("edit")} disabled={!p.permissions?.can_edit || !ctx.can_create} onClick={() => this.openEditor(p)}>
                                        <span className="icon">{""}</span>
                                    </Act>
                                )}
                                {canDeletePerm && (
                                    <Act className="btn btn-outline-danger btn-sm" title={t("delete")} disabled={!p.permissions?.can_delete} onClick={() => this.deleteDraft(p)}>
                                        <span className="icon">{""}</span>
                                    </Act>
                                )}
                            </div>
                        ))}
                        {saved.map((p) => (
                            <div key={p.uid} className="d-flex align-items-center gap-2 border rounded-3 p-2 mb-2">
                                <div className="flex-grow-1 min-w-0">
                                    <Act className="fw-bold text-primary text-decoration-underline" onClick={() => this.openView(p)}>
                                        {p.pl_number}
                                    </Act>
                                    <span className="badge bg-success-subtle text-success border ms-2">{t("pl_saved")}</span>
                                    {meta(p)}
                                </div>
                                <Act className="btn btn-outline-secondary btn-sm" title={t("view")} onClick={() => this.openView(p)}>
                                    <span className="icon">{""}</span>
                                </Act>
                                <Act className="btn btn-outline-secondary btn-sm" title={t("print")} onClick={() => this.printPl(p)}>
                                    <span className="icon">{""}</span>
                                </Act>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    renderEditor() {
        const { ctx, editor: ed } = this.state;
        if (!ed) return null;
        const E = ed.tried ? this.validate(true) : this.validate(false);
        const total = ed.items.reduce((a, i) => a + (Number(i.quantity) || 0), 0);
        const err = (k) => E[k] && <div className="text-danger text-12 mt-1">{E[k]}</div>;
        return (
            <Modal show size="xl" onHide={this.tryCloseEditor} centered scrollable>
                <Modal.Header className="bg-white">
                    <Modal.Title className="fs-5">
                        {ed.uid ? t("pl_edit_draft") : t("pl_create")} (Picking List Request) <span className="badge bg-secondary ms-2">{t("pl_draft")}</span>
                        <div className="text-12 text-secondary fw-normal">{t("pl_no_number_hint")}</div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label fw-bold text-13">{t("pl_ref_wo")}</label>
                            <input className="form-control" readOnly value={ctx.wo?.wo_number || ""} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold text-13">{t("pl_ref_so")}</label>
                            <input className="form-control" readOnly value={ctx.wo?.so_no || ""} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold text-13">{t("pl_requester")}</label>
                            <input className="form-control" readOnly value={ed.requester_name || ""} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold text-13">{t("pl_warehouse")}</label>
                            <select className="form-select" value={ed.warehouse_uid} onChange={(e) => this.onChangeWarehouse(e.target.value)}>
                                {(ctx.warehouses || []).map((w) => (
                                    <option key={w.uid} value={w.uid}>
                                        {w.code} {w.name}
                                    </option>
                                ))}
                            </select>
                            <div className="text-12 text-secondary mt-1">{t("pl_warehouse_hint")}</div>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold text-13">
                                {t("pl_need_date")} <span className="text-danger">*</span>
                            </label>
                            <DatePicker type="th" className="form-control" value={ed.need_date} min={todayStr()} onChange={(v) => this.patchEditor({ need_date: v })} />
                            {err("need_date") || <div className="text-12 text-secondary mt-1">{t("pl_hint_need_date")}</div>}
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold text-13">
                                {t("pl_reason")} <span className="text-danger">*</span>
                            </label>
                            <div className={`d-flex flex-wrap gap-3 ${E.reason ? "border border-danger rounded p-2" : ""}`}>
                                {(ctx.reasons || []).map((r) => (
                                    <label key={r.value} className="d-flex align-items-center gap-1 text-13" style={{ cursor: "pointer" }}>
                                        <input type="radio" name="pl_reason" checked={ed.reason === r.value} onChange={() => this.patchEditor({ reason: r.value })} /> {r.label}
                                    </label>
                                ))}
                            </div>
                            {err("reason")}
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold text-13">
                                {t("pl_cost_center")} <span className="text-danger">*</span>
                            </label>
                            <select className={`form-select ${E.cost_center ? "is-invalid" : ""}`} value={ed.cost_center} onChange={(e) => this.patchEditor({ cost_center: e.target.value })}>
                                <option value="">{t("pl_select")}</option>
                                {(ctx.cost_centers || []).map((c) => (
                                    <option key={c.code} value={c.code}>
                                        {c.code} - {c.name}
                                    </option>
                                ))}
                            </select>
                            {err("cost_center") || <div className="text-12 text-secondary mt-1">{t("pl_hint_cost_center")}</div>}
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-bold text-13">{t("pl_customer_site")}</label>
                            <input className="form-control" readOnly value={[ctx.wo?.customer_name, ctx.wo?.site].filter(Boolean).join(" · ")} />
                        </div>
                    </div>

                    <div className="mt-3 position-relative">
                        <label className="form-label fw-bold text-13">{t("pl_search_part_wh").replace("{code}", ctx.warehouses?.find((w) => w.uid === ed.warehouse_uid)?.code || "")}</label>
                        <input className="form-control" value={ed.q} placeholder={t("pl_search_placeholder")} autoComplete="off" onChange={(e) => this.onSearch(e.target.value)} onFocus={() => ed.results === null && this.onSearch(ed.q)} />
                        {ed.results !== null && (
                            <div className="border rounded-2 mt-1 bg-white" style={{ maxHeight: 230, overflow: "auto" }}>
                                {ed.searching && <div className="p-2 text-secondary text-13">{t("loading")}</div>}
                                {!ed.searching && !ed.results.length && <div className="p-2 text-secondary text-13">{t("pl_search_not_found")}</div>}
                                {ed.results.map((p) => {
                                    const added = ed.items.some((i) => i.part_uid === p.part_uid);
                                    return (
                                        <div key={p.part_uid} className={`d-flex align-items-center gap-2 px-2 py-1 border-bottom text-13 ${p.out_of_stock ? "text-secondary bg-light" : ""}`}>
                                            <span className="fw-bold" style={{ width: 120 }}>{p.part_number}</span>
                                            <span className="flex-grow-1">
                                                {p.part_name}
                                                {p.in_so_qty > 0 && <span className="badge bg-warning-subtle text-warning-emphasis ms-2">{t("pl_in_so")}</span>}
                                            </span>
                                            <span style={{ width: 150 }} className={`text-end ${p.out_of_stock ? "text-danger fw-bold" : ""}`}>
                                                {p.out_of_stock ? t("pl_out_of_stock") : `${t("pl_available")} ${formatNumber(p.available_qty)} ${p.uom_name || ""}`}
                                            </span>
                                            <button type="button" className="btn btn-outline-primary btn-sm" disabled={p.out_of_stock || added} onClick={() => this.addPart(p)}>
                                                {p.out_of_stock ? t("pl_cannot_select") : added ? t("pl_already_added") : `+ ${t("pl_add")}`}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="border rounded-3 mt-3 overflow-hidden">
                        <table className="table table-sm mb-0 align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center" style={{ width: 40 }}>#</th>
                                    <th>{t("pl_part_number")}</th>
                                    <th>{t("pl_part_name")}</th>
                                    <th>{t("pl_uom")}</th>
                                    <th className="text-end">{t("pl_stock_left")}</th>
                                    <th className="text-end" style={{ width: 130 }}>
                                        {t("pl_qty_request")} <span className="text-danger">*</span>
                                    </th>
                                    <th style={{ width: 44 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {!ed.items.length ? (
                                    <tr>
                                        <td colSpan={7} className="text-center text-secondary py-3">{t("pl_items_empty")}</td>
                                    </tr>
                                ) : (
                                    ed.items.map((it, ix) => (
                                        <tr key={it.part_uid} className={E["q" + ix] || E["a" + ix] ? "table-danger" : ""}>
                                            <td className="text-center">{ix + 1}</td>
                                            <td>{it.part_number}</td>
                                            <td>
                                                {it.part_name}
                                                {it.in_so_qty > 0 && (
                                                    <div className={`mt-1 px-2 py-1 rounded border text-12 ${E["a" + ix] ? "border-danger text-danger" : "border-warning bg-warning-subtle"}`}>
                                                        <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                                                            <input type="checkbox" checked={it.ack} onChange={(e) => this.setItem(ix, { ack: e.target.checked })} />
                                                            {t("pl_in_so_ack").replace("{qty}", formatNumber(it.in_so_qty)).replace("{unit}", it.uom_name || "")}
                                                        </label>
                                                    </div>
                                                )}
                                            </td>
                                            <td>{it.uom_name}</td>
                                            <td className="text-end">{it.available_qty == null ? "-" : formatNumber(it.available_qty)}</td>
                                            <td className="text-end">
                                                <input
                                                    type="number"
                                                    min={1}
                                                    step={1}
                                                    className={`form-control form-control-sm text-end ${E["q" + ix] ? "is-invalid" : ""}`}
                                                    value={it.quantity}
                                                    onChange={(e) => this.setItem(ix, { quantity: e.target.value === "" ? "" : Number(e.target.value) })}
                                                />
                                                {E["q" + ix] && <div className="text-danger text-12 mt-1">{E["q" + ix]}</div>}
                                            </td>
                                            <td className="text-center">
                                                <button type="button" className="btn btn-outline-danger btn-sm" title={t("delete")} onClick={() => this.removeItem(ix)}>
                                                    <span className="icon">{""}</span>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {err("items")}

                    <div className="mt-3">
                        <label className="form-label fw-bold text-13">
                            {t("pl_remark")} {ed.reason === REASON_OTHER && <span className="text-danger">*</span>}
                        </label>
                        <textarea className={`form-control ${E.remark ? "is-invalid" : ""}`} rows={2} placeholder={t("pl_remark_placeholder")} value={ed.remark} onChange={(e) => this.patchEditor({ remark: e.target.value })} />
                        {err("remark")}
                    </div>

                    {ed.banner && (
                        <div className={`alert ${ed.banner.kind === "error" ? "alert-danger" : "alert-warning"} mt-3 mb-0 py-2 text-13`}>
                            {ed.banner.text}
                            {ed.banner.items?.length > 0 && (
                                <ul className="mb-0 mt-1">
                                    {ed.banner.items.map((c) => (
                                        <li key={c.part_uid}>
                                            {c.part_number} {c.part_name}: {t("pl_requested")} {formatNumber(c.requested)} / {t("pl_available")} {formatNumber(c.available)}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                    <div className="text-end fw-bold mt-2">
                        {t("pl_total")} {formatNumber(ed.items.length)} {t("pl_items_unit")} · {formatNumber(total)} {t("pl_qty_unit")}
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-white">
                    <div className="text-12 text-secondary me-auto">{t("pl_save_locked_hint")}</div>
                    <button type="button" className="btn btn-outline-secondary" onClick={this.tryCloseEditor}>{t("cancel")}</button>
                    <button type="button" className="btn btn-outline-primary" disabled={ed.saving} onClick={this.saveDraft}>{t("pl_save_draft")}</button>
                    <button type="button" className="btn btn-primary" disabled={ed.saving} onClick={() => this.askSaveFinal(false)}>{t("pl_save_final")}</button>
                    <button type="button" className="btn btn-dark" disabled={ed.saving} onClick={() => this.askSaveFinal(true)}>{t("pl_save_and_print")}</button>
                </Modal.Footer>
            </Modal>
        );
    }

    renderView() {
        const pl = this.state.viewing;
        if (!pl) return null;
        const k = (label, value) => (
            <div className="col-md-4">
                <div className="text-12 text-secondary">{label}</div>
                <div className="fw-bold">{value || "-"}</div>
            </div>
        );
        return (
            <Modal show size="lg" onHide={() => this.setState({ viewing: null })} centered scrollable>
                <Modal.Header className="bg-white">
                    <Modal.Title className="fs-5">
                        {pl.pl_number} <span className="badge bg-success-subtle text-success border ms-2">{t("pl_saved")}</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row g-3">
                        {k(t("pl_ref_wo"), pl.wo_number)}
                        {k(t("pl_ref_so"), pl.so_no)}
                        {k(t("pl_customer"), this.state.ctx?.wo?.customer_name)}
                        {k(t("pl_warehouse"), `${pl.warehouse?.code || ""} ${pl.warehouse?.name || ""}`)}
                        {k(t("pl_cost_center"), this.ccLabel(pl.cost_center))}
                        {k(t("pl_requester"), pl.requester?.name)}
                        {k(t("pl_created_need_date"), `${fmtD(pl.saved_at || pl.created_at)} · ${fmtD(pl.need_date)}`)}
                        <div className="col-12">
                            <div className="text-12 text-secondary">{t("pl_reason")}</div>
                            <div className="fw-bold">{this.reasonLabel(pl.reason)}{pl.remark ? ` · ${pl.remark}` : ""}</div>
                        </div>
                    </div>
                    <table className="table table-sm mt-3 mb-0">
                        <thead className="table-light">
                            <tr>
                                <th className="text-center">#</th>
                                <th>{t("pl_part_number")}</th>
                                <th>{t("pl_part_name")}</th>
                                <th className="text-end">{t("pl_qty")}</th>
                                <th>{t("pl_uom")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(pl.items || []).map((i, ix) => (
                                <tr key={i.uid || ix}>
                                    <td className="text-center">{ix + 1}</td>
                                    <td>{i.part_number}</td>
                                    <td>{i.part_name}</td>
                                    <td className="text-end">{formatNumber(i.quantity)}</td>
                                    <td>{i.uom_name}</td>
                                </tr>
                            ))}
                            <tr className="fw-bold">
                                <td></td>
                                <td></td>
                                <td>{t("pl_total")} {formatNumber(pl.item_count)} {t("pl_items_unit")}</td>
                                <td className="text-end">{formatNumber(pl.total_quantity)}</td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                    <div className="alert alert-primary py-2 mt-3 mb-0 text-13">{t("pl_saved_locked_note")}</div>
                </Modal.Body>
                <Modal.Footer className="bg-white">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => this.setState({ viewing: null })}>{t("close")}</button>
                    <button type="button" className="btn btn-primary" onClick={() => this.printPl(pl)}>{t("print")}</button>
                </Modal.Footer>
            </Modal>
        );
    }

    renderConfirm() {
        const c = this.state.confirm;
        if (!c) return null;
        const done = () => this.setState({ confirm: null });
        return (
            <Modal show size="sm" onHide={done} centered style={{ zIndex: 1100 }}>
                <Modal.Header className="bg-white">
                    <Modal.Title className="fs-6">{c.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-14" style={{ whiteSpace: "pre-line" }}>
                    {c.body}
                    {c.warn && <div className="text-danger mt-3">{c.warn}</div>}
                </Modal.Body>
                <Modal.Footer className="bg-white">
                    <button type="button" className="btn btn-outline-secondary" onClick={done}>{c.cancel || t("cancel")}</button>
                    <button
                        type="button"
                        className={`btn ${c.danger ? "btn-danger" : "btn-primary"}`}
                        onClick={() => {
                            done();
                            c.onOk && c.onOk();
                        }}
                    >
                        {c.ok || t("confirm")}
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        const { ctx, ready } = this.state;
        if (!ready || !ctx || ctx.wo?.is_normal_sales === false || !can("wo_picking_list.read")) return null;
        return (
            <>
                {this.renderCard()}
                {this.renderEditor()}
                {this.renderView()}
                {this.renderConfirm()}
            </>
        );
    }
}
